import React, { useState } from 'react';
import {
  FileText,
  ShieldCheck,
  Copy,
  CheckCircle2,
  Download,
  Building2,
  Lock,
  Globe,
  AlertTriangle,
  Info
} from 'lucide-react';
import { monetizationEngine } from '../../services/monetizationEngine';

export const CommercialGovernanceHub: React.FC = () => {
  const [relationships] = useState(monetizationEngine.getCommercialRelationships());
  const [adsTxtEntries] = useState(monetizationEngine.getAdsTxtEntries());
  const [copiedNotice, setCopiedNotice] = useState<string | null>(null);

  const adsTxtContent = monetizationEngine.generateAdsTxtContent();

  const handleCopyAdsTxt = () => {
    navigator.clipboard.writeText(adsTxtContent);
    setCopiedNotice('ads.txt content copied to clipboard!');
    setTimeout(() => setCopiedNotice(null), 3000);
  };

  const handleDownloadAdsTxt = () => {
    const blob = new Blob([adsTxtContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ads.txt';
    a.click();
  };

  return (
    <div className="space-y-8 pb-12">
      {/* Banner */}
      <div className="bg-slate-900 text-white rounded-2xl p-8 shadow-xl space-y-4">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 text-xs font-bold border border-blue-500/30">
          <FileText className="w-3.5 h-3.5" />
          YamooPay Commercial Governance & Supply Chain Security
        </div>
        <h1 className="text-3xl font-extrabold tracking-tight">Commercial Governance & ads.txt Workstation</h1>
        <p className="text-slate-300 text-sm max-w-2xl leading-relaxed">
          Manage commercial contracts, generate validated ads.txt supply-chain declarations, and enforce editorial commercial firewalls.
        </p>
      </div>

      {/* ads.txt Governance Section */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 pb-4">
          <div>
            <div className="text-xs font-bold uppercase text-blue-600 tracking-wider">
              Authorized Digital Sellers (ads.txt)
            </div>
            <h2 className="text-xl font-bold text-slate-900 mt-1">
              Supply-Chain Integrity & Declaration
            </h2>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleCopyAdsTxt}
              className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer"
            >
              <Copy className="w-3.5 h-3.5" /> Copy Content
            </button>
            <button
              onClick={handleDownloadAdsTxt}
              className="px-3 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer"
            >
              <Download className="w-3.5 h-3.5 text-emerald-400" /> Download ads.txt
            </button>
          </div>
        </div>

        {/* Live ads.txt Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 font-bold uppercase text-[10px]">
                <th className="py-2.5 px-3">Domain</th>
                <th className="py-2.5 px-3">Publisher ID</th>
                <th className="py-2.5 px-3">Type</th>
                <th className="py-2.5 px-3">Certification ID</th>
                <th className="py-2.5 px-3">Provider Name</th>
                <th className="py-2.5 px-3">Audit Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
              {adsTxtEntries.map((entry) => (
                <tr key={entry.id} className="hover:bg-slate-50/80 transition-colors">
                  <td className="py-2.5 px-3 font-mono text-slate-900 font-bold">{entry.domain}</td>
                  <td className="py-2.5 px-3 font-mono text-slate-600">{entry.publisherId}</td>
                  <td className="py-2.5 px-3 font-bold text-slate-800">{entry.relationshipType}</td>
                  <td className="py-2.5 px-3 font-mono text-slate-400 text-[11px]">
                    {entry.certificationId || 'N/A'}
                  </td>
                  <td className="py-2.5 px-3">{entry.providerName}</td>
                  <td className="py-2.5 px-3">
                    <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px] font-bold">
                      {entry.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Formatted Code Box */}
        <div className="space-y-2">
          <div className="text-xs font-bold text-slate-700 flex items-center justify-between">
            <span>Formatted Output (`/ads.txt`)</span>
            {copiedNotice && <span className="text-emerald-700 font-bold">{copiedNotice}</span>}
          </div>
          <pre className="p-4 bg-slate-950 text-slate-200 rounded-xl font-mono text-xs overflow-x-auto border border-slate-800 leading-relaxed">
            {adsTxtContent}
          </pre>
        </div>
      </div>

      {/* Commercial Relationship Registry */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 space-y-6">
        <div className="border-b border-slate-100 pb-4">
          <h2 className="text-xl font-bold text-slate-900">Commercial Partner Relationship Registry</h2>
          <p className="text-slate-500 text-xs mt-1">
            Registered commercial agreements and active disclosure policies across all operating markets.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {relationships.map((rel) => (
            <div key={rel.id} className="p-5 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
              <div className="flex items-center justify-between border-b border-slate-200/80 pb-2">
                <div>
                  <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px] font-extrabold uppercase">
                    {rel.type}
                  </span>
                  <h3 className="font-bold text-slate-900 text-sm mt-1">{rel.partnerName}</h3>
                </div>
                <span className="px-2.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-xs font-bold">
                  {rel.status}
                </span>
              </div>

              <div className="space-y-1.5 text-xs text-slate-600">
                <div><strong>Markets:</strong> {rel.markets.join(', ')}</div>
                <div><strong>Commission Model:</strong> {rel.commissionModel}</div>
                <div><strong>Owner:</strong> {rel.agreementOwner}</div>
              </div>

              <div className="p-3 bg-white rounded-lg border border-slate-200 text-xs space-y-1">
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Mandatory Consumer Disclosure
                </div>
                <p className="text-slate-700 font-medium italic">"{rel.disclosureText}"</p>
              </div>

              <div className="text-[11px] font-bold text-emerald-700 flex items-center gap-1 pt-1">
                <CheckCircle2 className="w-3.5 h-3.5" /> Organic Ranking Independence Verified
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
